"use client";

import React from "react";
import Link from "next/link";
import { Reply, Ticket as TicketIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SafeEmailBody } from "@/components/crm/messages/components/SafeEmailBody";

import { SupportStatusBadge } from "./SupportStatusBadge";
import { SupportInboxAttachments } from "./SupportInboxAttachments";
import { formatWhen } from "./support-inbox-format";
import type { ReadingMessage } from "./types";

interface SupportInboxMessageCardProps {
  companyId: string;
  message: ReadingMessage;
  /** Tooltip naming the resolved customer a reply will actually reach. */
  replyTitle: string;
  onReply: (message: ReadingMessage) => void;
  onCreateTicket: (message: ReadingMessage) => void;
}

/** One message in the Support Inbox reading pane. */
export function SupportInboxMessageCard({
  companyId,
  message,
  replyTitle,
  onReply,
  onCreateTicket,
}: SupportInboxMessageCardProps) {
  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="text-sm font-medium text-card-foreground truncate">
              {message.subject || "(no subject)"}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {message.from}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {formatWhen(message.receivedAt)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <SupportStatusBadge status={message.badges.status} />
          </div>
        </div>

        <SafeEmailBody
          body={
            message.htmlContent ||
            message.textContent ||
            message.snippet ||
            ""
          }
          variant="full"
          collapseQuotedReplies
        />

        <SupportInboxAttachments
          companyId={companyId}
          threadId={message.threadId}
          messageId={message.messageId}
          attachments={message.attachments}
        />

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onReply(message)}
            title={replyTitle}
          >
            <Reply className="w-4 h-4 mr-2" />
            Reply
          </Button>
          {message.badges.ticketId ? (
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
            >
              <Link href={`/tickets/${message.badges.ticketId}`}>
                <TicketIcon className="w-4 h-4 mr-2" />
                View ticket
              </Link>
            </Button>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={() => onCreateTicket(message)}
            >
              <TicketIcon className="w-4 h-4 mr-2" />
              Create ticket
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
